import { useState, type ReactNode } from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Alert, cn, Drawer } from "@platform/ui";
import { useConsole } from "../context";
import { useT } from "../i18n";
import { Header } from "./Header";
import { Navigator } from "./Navigator";

const COLLAPSED_KEY = "console.sidebarCollapsed";

function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      } catch {
        // storage unavailable — keep the in-memory state
      }
      return !c;
    });
  return [collapsed, toggle];
}

export function Shell({ children }: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const sidebarWidth = collapsed ? "w-[var(--spacing-sidebar-collapsed)]" : "w-[var(--spacing-sidebar)]";
  const { t } = useT();
  const { me, config, productName } = useConsole();
  return (
    <div className="flex min-h-screen">
      <aside className={cn("hidden shrink-0 transition-[width] duration-200 lg:block", sidebarWidth)}>
        <div
          className={cn(
            "fixed inset-y-0 overflow-x-hidden overflow-y-auto transition-[width] duration-200",
            sidebarWidth,
          )}
        >
          <Navigator pathname={pathname} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        </div>
      </aside>
      <Drawer open={open} onOpenChange={setOpen} title={t("nav.main")}>
        <Navigator pathname={pathname} onNavigate={() => setOpen(false)} />
      </Drawer>
      <div className="flex min-w-0 flex-1 flex-col">
        <Header pathname={pathname} onOpenNav={() => setOpen(true)} />
        <main className="flex-1 px-4 py-6 sm:px-8 sm:py-8">
          {me.mfaRequired && (
            <div className="mx-auto mb-4 max-w-[var(--container-content)]">
              <Alert
                tone="warning"
                action={
                  <a
                    className="font-medium text-primary-dark underline"
                    href={`${config.authServerUrl}/two-factor/setup`}
                  >
                    {t("auth.mfaSetup")}
                  </a>
                }
              >
                {t("auth.mfaRequired")}
              </Alert>
            </div>
          )}
          {me.usingInitialPassword && (
            <div className="mx-auto mb-4 max-w-[var(--container-content)]">
              <Alert
                tone="warning"
                action={
                  <a
                    className="font-medium text-primary-dark underline"
                    href={`${config.authServerUrl}/change-password`}
                  >
                    {t("auth.changePassword")}
                  </a>
                }
              >
                {t("auth.initialPassword")}
              </Alert>
            </div>
          )}
          {children ?? <Outlet />}
        </main>
        <footer className="px-4 pb-6 text-center text-xs text-ink-2">
          {productName} · {t("footer.version", { version: __APP_VERSION__ })}
        </footer>
      </div>
    </div>
  );
}
